import { useContext, useRef, FC, ReactNode, useEffect } from "react";
import Image from "next/image";
import {
  Flex,
  Text,
  Popover,
  PopoverTrigger as OrigPopoverTrigger,
  Button,
  Portal,
  PopoverContent,
  Box,
  PopoverBody,
  useMediaQuery,
} from "@chakra-ui/react";
import { useWeb3React } from "@web3-react/core";
import { Web3Provider } from "@ethersproject/providers";
import { useLocalStorage } from "react-use";
import { FiChevronDown } from "react-icons/fi";

// Contexts
import { StakeContext } from "providers/StakeProvider";

// Images
import ethereumIcon from "assets/svg/ethereum-icon.svg";

// Contants
import { NETWORK_KEY } from "constants/constants";
import { NETWORK_CHANGE } from "constants/analytics";

export const PopoverTrigger: FC<{ children: ReactNode }> = OrigPopoverTrigger;

const NetworkToggler = () => {
  const [isMobileOrTablet] = useMediaQuery("(max-width: 992px)");

  const {
    walletInfo: { icon },
    toggleChain,
    isPolygonChainActive,
    isLedgerApp,
    track,
  } = useContext(StakeContext);

  const [selectedChain, setSelectedChain] = useLocalStorage(
    NETWORK_KEY,
    "0x89"
  );
  const ref = useRef(null);
  const { chainId, account } = useWeb3React<Web3Provider>();

  useEffect(() => {
    const chain = chainId === 1 ? "0x1" : "0x89";
    setSelectedChain(chain);
    if (isLedgerApp && chain !== selectedChain) {
      toggleChain();
    }
  }, [chainId, account, isLedgerApp]);

  if (isLedgerApp) return null;

  return (
    <>
      <Flex
        display={{ base: "none", md: "inherit" }}
        bg="brand.backgroundLight"
        padding="0.5rem 1rem"
        boxShadow="0px 4px 12px rgba(53, 56, 140, 0.05)"
        borderRadius="8px"
        gap="0.5rem"
        alignItems="center"
        fontSize="14px"
        fontFamily="regular"
      >
        <Text>Staking Network</Text>
        <Flex
          alignItems="center"
          gap="0.5rem"
          p="0.4rem 1rem"
          borderWidth="1px"
          borderStyle="solid"
          borderColor={
            isPolygonChainActive ? "brand.primary" : "brand.background"
          }
          borderRadius="0.5rem"
          cursor="pointer"
          onClick={
            isPolygonChainActive
              ? undefined
              : () => {
                  track(NETWORK_CHANGE, { network_switched_to: "polygon" });
                  toggleChain();
                }
          }
        >
          <Image src={icon} width="20px" height="20px" alt="token-icon" />
          {isMobileOrTablet && isPolygonChainActive ? null : (
            <Text>Polygon</Text>
          )}
        </Flex>
        <Flex
          alignItems="center"
          gap="0.5rem"
          p="0.4rem 1rem"
          borderWidth="1px"
          borderStyle="solid"
          borderColor={
            !isPolygonChainActive ? "brand.primary" : "brand.background"
          }
          borderRadius="0.5rem"
          cursor="pointer"
          onClick={
            !isPolygonChainActive
              ? undefined
              : () => {
                  track(NETWORK_CHANGE, { network_switched_to: "polygon-eth" });
                  toggleChain();
                }
          }
        >
          <Image
            src={ethereumIcon}
            width="20px"
            height="20px"
            alt="token-icon"
          />
          {isMobileOrTablet && !isPolygonChainActive ? null : <Text>ETH</Text>}
        </Flex>
      </Flex>
      <Popover
        placement="bottom-start"
        closeOnBlur={true}
        initialFocusRef={ref}
      >
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <Button
                display={{ base: "inherit", md: "none" }}
                bg="brand.backgroundLight"
                h={{ base: "36px", lg: "52px" }}
                _focus={{
                  bg: "brand.backgroundLight",
                }}
                _hover={{
                  bg: "brand.backgroundLight",
                }}
                p={{ base: "0.5rem", lg: "1rem" }}
              >
                <Flex alignItems="center" gap="0.5rem">
                  {isPolygonChainActive ? (
                    <Image
                      src={icon}
                      width="20px"
                      height="20px"
                      alt="token-icon"
                    />
                  ) : (
                    <Image
                      src={ethereumIcon}
                      width="20px"
                      height="20px"
                      alt="token-icon"
                    />
                  )}
                  <FiChevronDown />
                </Flex>
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent bg="brand.backgroundLight" w="213px">
                <PopoverBody p={{ base: "0.5rem", lg: "1rem" }}>
                  <Flex
                    mb="1rem"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={
                      isPolygonChainActive
                        ? undefined
                        : () => {
                            track(NETWORK_CHANGE, {
                              network_switched_to: "polygon",
                            });
                            toggleChain();
                            onClose();
                          }
                    }
                  >
                    <Flex alignItems="center" gap="0.5rem">
                      <Image
                        src={icon}
                        width="20px"
                        height="20px"
                        alt="token-icon"
                      />
                      <Text
                        color={
                          isPolygonChainActive
                            ? "inherit"
                            : "brand.textInactive"
                        }
                      >
                        Polygon
                      </Text>
                    </Flex>
                    {isPolygonChainActive && (
                      <Box
                        h="0.5rem"
                        w="0.5rem"
                        bg="#45BE21"
                        borderRadius="50%"
                      ></Box>
                    )}
                  </Flex>
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={
                      !isPolygonChainActive
                        ? undefined
                        : () => {
                            track(NETWORK_CHANGE, {
                              network_switched_to: "polygon-eth",
                            });
                            toggleChain();
                            onClose();
                          }
                    }
                  >
                    <Flex alignItems="center" gap="0.5rem">
                      <Image
                        src={ethereumIcon}
                        width="20px"
                        height="20px"
                        alt="token-icon"
                      />
                      <Text
                        color={
                          !isPolygonChainActive
                            ? "inherit"
                            : "brand.textInactive"
                        }
                      >
                        Ethereum
                      </Text>
                    </Flex>
                    {!isPolygonChainActive && (
                      <Box
                        h="0.5rem"
                        w="0.5rem"
                        bg="#45BE21"
                        borderRadius="50%"
                      ></Box>
                    )}
                  </Flex>
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </>
        )}
      </Popover>
    </>
  );
};

export default NetworkToggler;
